import React from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { useNavigate, useParams } from "react-router-dom";

import { store, useAppSelector } from '@/app/store';
import { padZeros, RoleContext } from '@/app/api/common';
import { copyProjectRoom, getProjectRoom, getUnixEUTime, HotelInfo, HotelInfoPublic, HotelLimits, HotelRoom } from '@/features/projects/projectsSlice';
import { getUrl, getField, setField } from '@/features/channels/channelsSlice';

import FlexRow from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import MenuItem from '@/components/Common/MenuItem';

import Button from '@/components/Common/Button';
import DeletedPanel from '@/components/Common/DeletedPanel';
import { ButtonsModal, DeleteModal, RightBar } from '@/components/Common/Modals';

import ProjectRoomClone from './ProjectRoomClone';

const numericSort = (a: string, b: string) => {
  const va = parseFloat(a)
  const vb = parseFloat(b)
  
  if (va == vb)
    return 0;
  
  return (va > vb) ? 1 : -1;
}

export interface NumberTextProps {
  children?:			React.ReactNode;
}

const NumberText = (props: NumberTextProps) => {
  return (
  <span className="text-[#1B69AA]">
    { props.children }
  </span>
  );
};

export interface RoomRow {
  id:					string;
  room_no:				string;
  created_on:			number | undefined;
  last_modified:		number | undefined;
  duid:					string;
  activated:			boolean;
  last_auth:			number | undefined;
  last_activated:		number | undefined;
  platform:				string;
  last_ip:				string;
  last_mac:				string;
  serial:				string;
  client_version:		string;
  firmware_version:		string;
  device_model:			string;
  deviation:			string;
  options:				string;
  room:					HotelRoom;
  requestDelete:		(room_no: string) => any;
  requestDeactivate:	(room_no: string) => any;
  requestReactivate:	(room_no: string) => any;
}

export interface ProjectRoomsProps {
  project?:				HotelInfo;
  setProject?:			(val: HotelInfo) => any;
  pub?:					HotelInfoPublic | null;
  limits?:				HotelLimits | null;
  setLimits?:			(lim: HotelLimits) => any;
  setRoom?:				(room: HotelRoom) => Promise<boolean>;
  hideContainer?:		boolean;
  csvExport?:			number;
  csvFile?:				string;
}

const ProjectRooms = (props: ProjectRoomsProps) => {
  const { t } = useTranslation();
  
  const navigate = useNavigate();
  const projects = useAppSelector((state) => state.projects);
  const role = React.useContext(RoleContext);
  const canCreateRoom = role.admin || role?.privilages?.includes("EditProjectSettings");
  
  const { tab_no, sub_tab } = useParams();

  const projectId = props.pub?.id || props.project?.id || "";

  // Fetch latest project data (including rooms) when entering the rooms list
  React.useEffect(() => {
    if (props.project?.id) {
      store.getProjects(props.project.id);
    }
  }, [props.project?.id]);

  const [cols, setCols] = React.useState([] as any[]);
  
  const [rows, setRows] = React.useState([] as RoomRow[]);
  const [toDelete, setToDelete] = React.useState("");
  const [toDeactivate, setToDeactivate] = React.useState("");
  const [toReactivate, setToReactivate] = React.useState("");
  
  const [csvExport, setCsvExport] = React.useState(0);
  
  React.useEffect(() => {
    const rws: RoomRow[] = [];
    setCols([
    {
        field:			"room_no",
        headerName:		t("projects:project:rooms:grid:room_no"),
        comparator:		numericSort,
        pinned:			'left',
        lockPinned:		true,
        flex:			0,
        minWidth:		100,
    },
    {
        field:			"created_on",
        headerName:		t("projects:project:rooms:grid:created_on"),
        valueFormatter:	(p: any) => getUnixEUTime(p.value),
        flex:			1,
        minWidth:		170,
        maxWidth:		250,
    },
    {
        field:			"last_modified",
        headerName:		t("projects:project:rooms:grid:last_modified"),
        valueFormatter:	(p: any) => getUnixEUTime(p.value),
        flex:			1,
        minWidth:		170,
        maxWidth:		250,
    },
    /*{
        field:			"duid",
        headerName:		t("projects:project:rooms:grid:duid"),
        flex:			1,
        minWidth:		170,
        //maxWidth:		250,
    },*/
    {
        field:			"activated",
        headerName:		t("projects:project:rooms:grid:activated"),
        flex:			1,
        minWidth:		100,
        //maxWidth:		250,
    },
    {
        field:			"last_auth",
        headerName:		t("projects:project:rooms:grid:last_auth"),
        valueFormatter:	(p: any) => getUnixEUTime(p.value),
        flex:			1,
        minWidth:		170,
        maxWidth:		250,
    },
    {
        field:			"last_activated",
        headerName:		t("projects:project:rooms:grid:last_activated"),
        valueFormatter:	(p: any) => getUnixEUTime(p.value),
        flex:			1,
        minWidth:		170,
        maxWidth:		250,
    },
    {
        field:			"platform",
        headerName:		t("projects:project:rooms:grid:platform"),
        flex:			1,
        minWidth:		100,
        maxWidth:		180,
    },
    {
        field:			"last_ip",
        headerName:		t("projects:project:rooms:grid:last_ip"),
        flex:			1,
        minWidth:		150,
        maxWidth:		250,
    },
    {
        field:			"last_mac",
        headerName:		t("projects:project:rooms:grid:last_mac"),
        flex:			1,
        minWidth:		150,
        maxWidth:		250,
    },
    {
        field:			"serial",
        headerName:		t("projects:project:rooms:grid:last_serial"),
        flex:			1,
        minWidth:		170,
        maxWidth:		250,
    },
    {
        field:			"client_version",
        headerName:		t("projects:project:rooms:grid:client_version"),
        flex:			1,
        minWidth:		120,
        maxWidth:		250,
    },
    {
        field:			"firmware_version",
        headerName:		t("projects:project:rooms:grid:firmware_version"),
        flex:			1,
        minWidth:		170,
        maxWidth:		250,
    },
    {
        field:			"device_model",
        headerName:		t("projects:project:rooms:grid:device_model"),
        flex:			1,
        minWidth:		120,
        maxWidth:		180,
    },
    {
        field:			"deviation",
        headerName:		t("projects:project:rooms:grid:deviation"),
        flex:			0,
        minWidth:		100,
        maxWidth:		120,
    },
    ...(
      !props.hideContainer
      ?[{
        field:			"options",
        headerName:		t("projects:project:rooms:grid:options"),
        flex:			0,
        minWidth:		90,
        maxWidth:		90,
        cellRenderer:	OptionsCell,
    }] : []),
    ]);
    
    const roomsList = props.hideContainer && projects.channelListRooms
      ? projects.channelListRooms
      : (props.pub && props.pub.rooms && props.pub.rooms.rooms ? props.pub.rooms.rooms : []);

    for (let room of roomsList) {
        const fields =	room.info &&
        				room.info.fields
        				?	room.info.fields
        				:	[];

        const row: RoomRow = {
      	  id:						props.pub?.id || room.id || '',
      	  room_no:					room.room_no,
	      created_on:				room.created_on,
	      last_modified:			room.last_modified,
	      duid:						room.duid,
	      activated:				room.duid && room.duid.trim() != "" ? true : false,
	      last_auth:				room.last_auth,
	      last_activated:			room.last_activated,
	      platform:					getField(fields, "last_platform"),
	      last_ip:					getField(fields, "last_ip"),
	      last_mac:					getField(fields, "last_hw_addr"),
	      serial:					getField(fields, "last_serial"),
	      client_version:			getField(fields, "last_sw_version"),
	      firmware_version:			getField(fields, "last_fw_version"),
	      device_model:				getField(fields, "last_model"),
	      deviation:				room.deviation === true ? "true" : "false",
	      options:					"",
	      room:						room,
	      requestDelete:			(room_no: string) => {
	        console.log("ProjectRooms/requestDelete/room_no=" + room_no);
	        setToDelete(room_no);
	      },
	      requestDeactivate:		(room_no: string) => {
	        console.log("ProjectRooms/requestDeactivate/room_no=" + room_no);
	        setToDeactivate(room_no);
	      },
	      requestReactivate:		(room_no: string) => {
	        console.log("ProjectRooms/requestReactivate/room_no=" + room_no);
	        setToReactivate(room_no);
	      },
        };
      
        rws.push(row);
     
    }
    
    setRows(rws);
  }, [props.pub, props.hideContainer, projects.channelListRooms, t]);
  
  const handleDelete = (evt: Object, answer: string) => {
    if (	!toDelete ||
        	!props.pub
       )
      return;
    
    const room_no = toDelete;
    setToDelete("");
    
    if (answer !== "yes") {
      return;
    }
    
    const rume = getProjectRoom(props.pub, room_no);
    if (!rume)
      return;
    
    let newRoom = copyProjectRoom(rume);
    newRoom.to_delete = true;
    
    props.setRoom && props.setRoom(newRoom);
  };

  const handleDeactivate = (evt: Object, answer: string) => {
    if (	!toDeactivate ||
        	!props.pub
       )
      return;
    
    const room_no = toDeactivate;
    setToDeactivate("");
    
    if (answer !== "yes") {
      return;
    }
    
    const rume = getProjectRoom(props.pub, room_no);
    if (!rume)
      return;
    
    let newRoom = copyProjectRoom(rume);
    const oldDuid = newRoom.duid;
    
    newRoom.duid = "";
    if (oldDuid.trim() != "") {
      newRoom.fields = setField(newRoom.fields, "duid.old", oldDuid);
    }
    
    props.setRoom && props.setRoom(newRoom);
  };

  const handleReactivate = (evt: Object, answer: string) => {
    if (	!toReactivate ||
        	!props.pub
       )
      return;
    
    const room_no = toReactivate;
    setToReactivate("");
    
    if (answer !== "yes") {
      return;
    }
    
    const rume = getProjectRoom(props.pub, room_no);
    if (!rume)
      return;
    
    let newRoom = copyProjectRoom(rume);
    const oldDuid = getField(newRoom.fields, "duid.old");
    
    if (oldDuid.trim() == "")
      return;
    
    newRoom.duid = oldDuid;
    newRoom.fields = setField(newRoom.fields, "duid.old", "");
    props.setRoom && props.setRoom(newRoom);
  };
  
  const getRowId = React.useCallback((p: any) => p.data.room_no, []);

  const handleCellClick = (evt: any) => {
    if (evt.colDef.field == "options")
      return;
    navigate(getRoomUrl(evt.data.id, evt.data.room_no));
    
    //`/projects/${evt.data.id}/rooms/${evt.data.room_no}`);
  };
  
  const updateIsOpen = (val: boolean) => {
    if (	!props.pub ||
    		!props.pub.id
       )
      return;
    
    if (!val)
      navigate(`/projects/${encodeURIComponent(props.pub.id)}/rooms`);
  };
  
  return (
  <FlexRow direction="column"
           align="start"
           className="">
    {/*
    <Button className="bg-[#1B69AA] uppercase font-bold rounded-[5px] px-2 mb-[16px]"
            onClick={() => }>
      <FlexRow justify="center">
        <div className="material-icons !text-white">
          add
        </div>
        <div className="m14 text-white leading-normal">
          { t('projects:project:rooms:new') }
        </div>
      </FlexRow>
    </Button>
    */}
    { !props.hideContainer &&
    <FlexRow className="py-[16px]">
      {canCreateRoom &&
      <Button caption={t('projects:project:rooms:new')}
              className="uppercase"
              icon="add"
              onClick={() => props.pub && navigate(getRoomUrl(props.pub.id, "__new__"))} />
      }
      <FlexRow />
      <Button caption={t('projects:project:rooms:export')}
              className="uppercase"
              icon="share"
              onClick={() => setCsvExport(csvExport + 1)} />
    </FlexRow>
}
    <Grid rowData={rows}
          columnDefs={cols}
          getRowId={getRowId}
          filter={true}
          showPages={true}
          pageSize={10}
          maxHeight={props.hideContainer ? "calc(100vh - 280px)" : "calc(100vh - 460px)"}
          onCellClicked={handleCellClick}
          csvFile={props.csvFile || (props.pub ? props.pub.name + '-rooms.csv' : '')}
          csvExport={props.csvExport !== undefined ? props.csvExport : csvExport} />
    { !props.hideContainer && projectId &&
    <FlexRow className="pt-[16px]">
      <DeletedPanel kind="room"
                    hotelId={projectId}
                    icon="meeting_room"
                    title={t('deleted:kind:room')}
                    subtitle={t('deleted:kind:room_hint')}
                    canView={true}
                    onChanged={() => store.getProjects(projectId)} />
    </FlexRow>
    }
    {sub_tab && sub_tab == "clone" && props.pub && props.setRoom &&
	<RightBar onOpenChange={updateIsOpen}>
	  <ProjectRoomClone pub={props.pub}
	                    setRoom={props.setRoom} />
	</RightBar>
    }
    {toDeactivate != '' &&
	  <ButtonsModal title={t('projects:project:rooms:deactivate_room:caption')}
	                message={t('projects:project:rooms:deactivate_room:message_n', {n: toDeactivate})}
	                onResult={handleDeactivate}>
        <>
        </>
	  </ButtonsModal>
	}
	{toReactivate != '' &&
	  <ButtonsModal title={t('projects:project:rooms:reactivate_room:caption')}
	                message={t('projects:project:rooms:reactivate_room:message_n', {n: toReactivate})}
	                onResult={handleReactivate}>
        <>
        </>
	  </ButtonsModal>
	}
	{toDelete != '' &&
	  <DeleteModal title={t('projects:project:rooms:delete_room:caption')}
	               message={t('projects:project:rooms:delete_room:message_n', {n: toDelete})}
	               onResult={handleDelete}>
        <>
        </>
	  </DeleteModal>
	}
  </FlexRow>
  );
};

export const getRoomUrl = (project_id: string, room_no: string, action?: string): string => {
   const safeRoomNo = encodeURIComponent(room_no);
   const projectId = encodeURIComponent(project_id);
  let url = `/projects/${projectId}/rooms/${safeRoomNo}`;
  if (action)
    url += `/${action}`;
  return url;
};

const OptionsCell = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [open, setOpen] = React.useState(false);
  
  const reactivate = React.useMemo(() => {
    if (	props.data.duid.trim() != "" ||
    		!props.data.room ||
    		!props.data.room.fields
       )
      return false;
    
    const oldDuid = getField(props.data.room.fields, "duid.old");
    
    return oldDuid.trim() != "";
  }, [props.data.duid, props.data.room]);
  
  const handleEdit = () => navigate(getRoomUrl(props.data.id, props.data.room_no));
  const handleClone = () => {
    setOpen(false);
    navigate(getRoomUrl(props.data.id, props.data.room_no, "clone"));
    //navigate(`/projects/${props.data.id}/rooms/${props.data.room_no}`);
  };
  
  const handleDelete = () => {
    setOpen(false);
    props.data.requestDelete(props.data.room_no);
  };
  
  const handleDeReactivate = () => {
    setOpen(false);
    if (reactivate) {
      props.data.requestReactivate(props.data.room_no);
      return;
    }
    props.data.requestDeactivate(props.data.room_no);
  };
  
  return (
  <Popover key={1}
           isOpen={open}
           onOpenChange={setOpen}
           placement="bottom-end"
           classNames={{
             content: "rounded-none",
           }}>
    <PopoverTrigger>
      <div className="h-full w-full text-right">
        <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle">
          more_vert
        </span>
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[205px] py-[8px] gap-[8px]">
        <MenuItem icon="edit_note"
                  label={t('projects:project:rooms:edit_room')}
                  onClick={handleEdit} />
        <MenuItem icon="content_copy"
                  label={t('projects:project:rooms:clone_room:caption')}
                  onClick={handleClone} />
        <MenuItem icon={reactivate ? 'public' : 'public_off'}
                  label={t('projects:project:rooms:' + (reactivate ? 'r' : 'd') + 'eactivate_room:caption')}
                  onClick={handleDeReactivate} />
        <MenuItem icon="delete"
                  color="text-[--Warning]"
                  label={t('projects:project:rooms:delete_room:caption')}
                  onClick={handleDelete} />
      </FlexRow>
    </PopoverContent>
  </Popover>
  );
};

export default ProjectRooms;



